import { X } from 'lucide-react';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { cn } from '@rocco/utils/cn';
import { Drawer, DrawerClose, DrawerContent } from '@rocco/components/drawer';
import { DrawerTitle, DrawerDescription } from '@rocco/components/drawer';
import type { CartItem } from '~/modules/ecommerce/type/cart-item';
import { WHITE_DISC } from '~/ui-rocco/pages/detail-shared';
import { DrawerCartPanel } from '~/ui-rocco/pages/details-page/revamp/sections/purchase/DrawerCartPanel';

interface CartDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    items: CartItem[];
    subtotal?: string | null;
}

/**
 * The cart, from anywhere.
 *
 * The same panel the product drawer slides to after an add, in the same
 * shell — so the cart a reader opens from the header is the cart they were
 * just shown, rather than a second design of the same list. On the desktop
 * that shell floats at the right; on the phone it is the product drawer's
 * bottom sheet, capped under the header exactly as that one is.
 */
export const CartDrawer = ({
    open,
    onOpenChange,
    items,
    subtotal,
}: CartDrawerProps) => {
    const { mobile } = useAppDisplayModeContext();

    return (
        <Drawer
            direction={mobile ? 'bottom' : 'right'}
            handleOnly={!mobile}
            open={open}
            onOpenChange={onOpenChange}
        >
            <DrawerContent
                noHandle
                scrollable={!mobile}
                hideDefaultCloseButton
                style={
                    mobile
                        ? undefined
                        : { width: '25.875rem', maxWidth: '25.875rem' }
                }
                className={cn(
                    !mobile && [
                        'data-[state=open]:animate-in data-[state=open]:duration-300 data-[state=open]:slide-in-from-right',
                        'data-[state=closed]:animate-out data-[state=closed]:duration-200 data-[state=closed]:slide-out-to-right',
                        // The product drawer's own shell: floating, rounded,
                        // held off three edges.
                        'overflow-hidden rounded-[1.5rem]',
                        'data-[vaul-drawer-direction=right]:inset-y-6 data-[vaul-drawer-direction=right]:right-6',
                    ],
                )}
            >
                <DrawerTitle className="sr-only">Shopping cart</DrawerTitle>
                <DrawerDescription className="hidden" aria-hidden />
                <DrawerClose
                    aria-label="Close"
                    className={cn(
                        'absolute z-20',
                        // On the phone a plain X in the title's row, as the
                        // product drawer's cart pane draws it: the sheet is
                        // white, where the disc would vanish.
                        mobile
                            ? 'top-4 right-3 inline-flex size-10 cursor-pointer items-center justify-center rounded-full text-[#181818]'
                            : cn(WHITE_DISC, 'top-3 left-3'),
                    )}
                >
                    <X className="size-[1.25rem]" />
                </DrawerClose>
                {/* The sheet's height cap lives on this column: the list
                    inside scrolls, the footer stays pinned. 4.75rem is the
                    mobile header's height.

                    `h-full` on desktop, because this column's parent there is
                    `DrawerContent`'s own scroll block rather than a flex
                    container — so `flex-1` buys nothing and the column fell to
                    its content's height. The panel's `h-full` then had nothing
                    definite to resolve against and did the same, which
                    unpinned the footer: measured at 1920, one cart line left
                    the View Cart row 722px above the bottom of the drawer, and
                    twenty pushed it off the end of it. */}
                <div
                    className={cn(
                        'flex min-h-0 flex-1 flex-col',
                        mobile ? 'max-h-[calc(100dvh-4.75rem)]' : 'h-full',
                    )}
                >
                    <DrawerCartPanel
                        items={items}
                        subtotal={subtotal}
                        onClose={() => onOpenChange(false)}
                    />
                </div>
            </DrawerContent>
        </Drawer>
    );
};
